/**
 * Edit Website Modal component
 * Dialog for editing an existing website's details
 */

import * as Dialog from '@radix-ui/react-dialog';
import { useState, FormEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import Input from '../Input';
import Textarea from '../Textarea';

interface EditWebsiteModalProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (websiteData: {
    id: string;
    name: string;
    description?: string;
  }) => Promise<void>;
  website: {
    id: string;
    name: string;
    domain: string;
    description?: string;
  } | null;
}

export default function EditWebsiteModal({ open, onClose, onUpdate, website }: EditWebsiteModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Update form when website changes
  useEffect(() => {
    if (website) {
      setName(website.name);
      setDescription(website.description || '');
    }
  }, [website]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!website) return;

    // Validation
    if (!name.trim()) {
      setError(t('editWebsite.errors.nameRequired', 'Website name is required'));
      return;
    }

    if (name.trim().length < 2) {
      setError(t('editWebsite.errors.nameTooShort', 'Website name must be at least 2 characters'));
      return;
    }

    // Validate description length if provided
    if (description.trim() && description.trim().length < 20) {
      setError(t('editWebsite.errors.descriptionTooShort', 'Description must be at least 20 characters'));
      return;
    }

    if (description.trim() && description.trim().length > 300) {
      setError(t('editWebsite.errors.descriptionTooLong', 'Description must be less than 300 characters'));
      return;
    }

    setIsUpdating(true);

    try {
      await onUpdate({
        id: website.id,
        name: name.trim(),
        description: description.trim() || undefined,
      });

      // Show success message
      setSuccess(true);
      setError('');

      // Auto-close after 1.5 seconds
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || t('editWebsite.errors.failed', 'Failed to update website'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  if (!website) return null;

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 animate-in fade-in-0" />

        <Dialog.Content
          className="
            fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
            w-full max-w-md
            bg-white dark:bg-gray-900
            border-2 border-black dark:border-white
            rounded-lg shadow-brutal
            p-6
            z-50
            animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2
          "
        >
          <Dialog.Title className="text-2xl font-bold mb-2 dark:text-white">
            {t('editWebsite.title', 'Edit website')}
          </Dialog.Title>

          <Dialog.Description className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {t('editWebsite.description', 'Update the details of your website')}
          </Dialog.Description>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('editWebsite.domainLabel', 'Domain')}
              </label>
              <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm text-gray-600 dark:text-gray-400">
                {website.domain}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('editWebsite.domainHelper', 'Domain cannot be changed')}
              </p>
            </div>

            <Input
              label={t('editWebsite.nameLabel', 'Website Name') + ' *'}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Awesome Website"
              disabled={isUpdating}
              autoFocus
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('editWebsite.descriptionLabel', 'Description')}
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('editWebsite.descriptionPlaceholder', 'Briefly describe your website...')}
                rows={3}
                disabled={isUpdating}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('editWebsite.descriptionHelper', 'Optional (20-300 characters)')}
              </p>
            </div>

            {error && (
              <div className="text-sm text-red-600 dark:text-red-400 p-3 border-2 border-red-600 dark:border-red-400 rounded-lg bg-red-50 dark:bg-red-900/20">
                {error}
              </div>
            )}

            {success && (
              <div className="text-sm text-green-600 dark:text-green-400 p-3 border-2 border-green-600 dark:border-green-400 rounded-lg bg-green-50 dark:bg-green-900/20">
                ✓ {t('editWebsite.success', 'Website updated successfully!')}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isUpdating}
              >
                {t('common.cancel', 'Cancel')}
              </Button>

              <Button
                type="submit"
                variant="primary"
                disabled={isUpdating}
              >
                {isUpdating
                  ? t('editWebsite.updating', 'Updating...')
                  : t('editWebsite.update', 'Update website')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
